import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Ban, Mail, MessageSquare, Search, UserX } from "lucide-react";
import { fetchUnsubscribed } from "../api/communicationsApi";
import AgencyScopeSwitch, { useAgencyScope } from "../../../components/common/AgencyScopeSwitch";
import ContactLink from "../../contacts/components/ContactLink";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

/**
 * "Unsubscribed" - everyone who has asked us to stop, in one list.
 *
 * Paul Jimoulis (CRM-2026-00093, Monday 13213778648, 6 Oct 2026): "People who
 * unsubscribe from our email and sms communication needs to be automatically
 * unsubscribed so they no longer recieve communication from us. Please confirm
 * this is being tracked as I cant see who has unsubscribed."
 *
 * The blocking was already automatic - every send checks the opt-out lists
 * first. What was missing was any way to see them: someone who clicks the email
 * link is archived, so they disappear from Contacts at the exact moment they
 * leave. This lists them all, with how and when, and the send it came from.
 *
 * Agents see the people they have messaged; the agency principal can switch to
 * the whole office, the same rule as the rest of the Hub.
 */

const CHANNELS = [
  { id: "", label: "All" },
  { id: "email", label: "Email" },
  { id: "sms", label: "Text" },
  { id: "portal", label: "Buyer portal" },
];

const HOW = [
  { id: "optouts", label: "All opt-outs" },
  { id: "self", label: "They unsubscribed themselves" },
  { id: "staff", label: "Unsubscribed by staff" },
  { id: "bounce", label: "Bounced / dead addresses" },
];

const WINDOWS = [
  { id: 0, label: "All time" },
  { id: 7, label: "Last 7 days" },
  { id: 30, label: "Last 30 days" },
  { id: 90, label: "Last 90 days" },
];

const PAGE_SIZE = 50;

function ChannelBadge({ channel }) {
  if (channel === "sms") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
        <MessageSquare className="w-3 h-3" /> Text
      </span>
    );
  }
  if (channel === "portal") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
        <UserX className="w-3 h-3" /> Portal
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700">
      <Mail className="w-3 h-3" /> Email
    </span>
  );
}

function Tile({ label, value, hint }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-2xl font-bold text-gray-900 tabular-nums">{value ?? "–"}</p>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

export default function UnsubscribedTab() {
  const { agencyWide, setAgencyWide, canSwitch } = useAgencyScope("unsubscribed");
  const [channel, setChannel] = useState("");
  const [how, setHow] = useState("optouts");
  const [days, setDays] = useState(0);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Search on a pause in typing, not every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [channel, how, days, q, agencyWide]);

  const params = useMemo(() => {
    const p = { how, page, page_size: PAGE_SIZE };
    if (channel) p.channel = channel;
    if (days) p.days = days;
    if (q) p.q = q;
    if (canSwitch) p.agency_wide = agencyWide ? "true" : "false";
    return p;
  }, [channel, how, days, q, page, canSwitch, agencyWide]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchUnsubscribed(params)
      .then((d) => active && setData(d))
      .catch(() => active && toast.error("Couldn't load the unsubscribed list"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [params]);

  const rows = data?.results || [];
  const summary = data?.summary || {};
  const count = data?.count || 0;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="max-w-2xl">
          <p className="text-sm text-gray-600">
            Everyone who has asked to stop hearing from us. They are blocked automatically: no
            campaign, bulk send or one-off message reaches them on that channel again.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {data?.agency_wide
              ? "Showing the whole office."
              : "Showing people you have sent emails or texts to."}
          </p>
        </div>
        {canSwitch && (
          <AgencyScopeSwitch
            agencyWide={agencyWide}
            onChange={setAgencyWide}
            mineLabel="My contacts"
            agencyLabel="All agents"
          />
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Tile label="Unsubscribed" value={summary.total} hint="All time" />
        <Tile label="Last 30 days" value={summary.last_30_days} />
        <Tile label="From email" value={summary.email} hint={`plus ${summary.portal ?? 0} from the buyer portal`} />
        <Tile label="From texts" value={summary.sms} />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {CHANNELS.map((c) => (
            <button
              key={c.id || "all"}
              type="button"
              onClick={() => setChannel(c.id)}
              className={`px-3 py-1 rounded-md text-xs font-bold transition ${
                channel === c.id ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <select
          value={how}
          onChange={(e) => setHow(e.target.value)}
          aria-label="How they unsubscribed"
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white"
        >
          {HOW.map((h) => (
            <option key={h.id} value={h.id}>
              {h.label}
              {h.id === "bounce" && summary.bounced ? ` (${summary.bounced})` : ""}
            </option>
          ))}
        </select>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="When"
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white"
        >
          {WINDOWS.map((w) => (
            <option key={w.id} value={w.id}>{w.label}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or mobile"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading && !data ? (
          <p className="text-sm text-gray-400 p-6">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Ban className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Nobody matches these filters.</p>
          </div>
        ) : (
          <table className={`w-full text-sm ${loading ? "opacity-60" : ""}`}>
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Person</th>
                <th className="text-left px-4 py-3">Channel</th>
                <th className="text-left px-4 py-3">How</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">From</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const address = r.email || r.phone;
                return (
                  <tr key={r.key} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3">
                      {r.name ? (
                        <ContactLink
                          identity={{ contactId: r.contact_id, email: r.email, name: r.name, phone: r.phone }}
                          className="font-semibold text-[#3a99a3] hover:text-[#55C5D0] hover:underline text-left"
                        >
                          {r.name}
                        </ContactLink>
                      ) : (
                        <span className="font-semibold text-gray-500">No contact on file</span>
                      )}
                      <p className="text-xs text-gray-500 break-all">{address}</p>
                      {r.archived && (
                        <p className="text-[11px] text-gray-400">Archived from Contacts</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ChannelBadge channel={r.channel} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-700">{r.how}</p>
                      {r.note && <p className="text-xs text-gray-400">{r.note}</p>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {r.unsubscribed_at ? formatMelbourneDateTime(r.unsubscribed_at) : "–"}
                    </td>
                    <td className="px-4 py-3">
                      {r.from_send ? (
                        <>
                          <p className="text-gray-700">{r.from_send}</p>
                          <p className="text-xs text-gray-400">
                            {r.from_send_credited ? "Unsubscribed from this send" : "Last message we sent them"}
                            {r.last_sent_at ? `, ${formatMelbourneDateTime(r.last_sent_at)}` : ""}
                          </p>
                        </>
                      ) : (
                        <span className="text-gray-400">–</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {count > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
          <span>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of {count}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
